import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Input, Modal } from '../../../../../common/_custom';
import styles from './AddToWatchlist.module.scss';
import { addCompanyToWatchlist } from '../../../../../store/investor/investorApiHelper';
import { IDENTIFIER, WEBSITE, WEBSITE_ERROR_MSG } from '../../../../../utils/constants/companyLogin';
import { Mixpanel } from '../../../../../utils/mixpanel';
import { INVESTOR_ADD_TO_WATCHLIST_CLICK } from '../../../../../utils/constants/mixpanelEvents/investorEvents';
import { infoPurpleIcon, plusPurpleCircle } from '../../../../../assets/hostedassets';
import useSearchResultFetcher from '../../../../../common/GlobalSearch/useSearchResultFetcher';
import { AddToWatchlistPrefill } from './prefillBuilder';
import { extractIdentifier, resolveAddLeadIdentifier } from './identifierUtils';
import AddToWatchlistDropdown from './AddToWatchlistDropdown';
import { GLOBAL_SEARCH_GROUPS } from '../../../../../common/MagicUpload/Utils/MagicUploadConstants';
import { useAppSelector } from '../../../../../app/hooks';

export enum ADD_JOURNEY_V2 {
    ADD_TO_SCAN = 'ADD_TO_SCAN',
    ADD_TO_WATCH = 'ADD_TO_WATCH',
}

function AddToWatchlistV2({
    prefill,
    modalOpen,
    closeModal,
    investorOrgId,
    onSuccess = () => {},
    handleAlreadyAddedInPlace = false,
    source,
}: {
    prefill?: AddToWatchlistPrefill;
    modalOpen?: boolean;
    closeModal: any;
    investorOrgId: string;
    onSuccess?: (data?: any) => void;
    handleAlreadyAddedInPlace?: boolean;
    source?: string;
}) {
    const location = useLocation();
    const userEmail = useAppSelector((state) => (state.user as any)?.userData?.emailId || (state.user as any)?.userData?.email);
    const isProspectsPage = location.pathname?.toLowerCase?.().includes('/watch');
    const journey = isProspectsPage ? ADD_JOURNEY_V2.ADD_TO_WATCH : ADD_JOURNEY_V2.ADD_TO_SCAN;
    const IDENTIFY_ERROR_MSG = "Entity couldn't be identified. Please try again with a different Legal name.";
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        [WEBSITE]: '',
        [IDENTIFIER]: '',
        brandName: '',
    });
    const [formErrors, setFormErrors] = useState({
        [WEBSITE]: '',
        [IDENTIFIER]: '',
        brandName: '',
    });
    const inviteCode = sessionStorage.getItem('invite_code') ?? localStorage.getItem('invite_code') ?? '';
    const hasError = Object.values(formErrors).some((e) => !!e);

    const {
        searchText,
        setSearchText,
        searchResults,
        searchLoading,
        setSearchResults,
        getTextForKey,
        fetchCompanyInfoForResult,
        navigateUser,
    } = useSearchResultFetcher();
    const [showGlobalDropdown, setShowGlobalDropdown] = useState(false);
    const [infoLoading, setInfoLoading] = useState({ website: false, brand: false });
    const [selectedAddNewResult, setSelectedAddNewResult] = useState<any>(null);
    const [prefillLoading, setPrefillLoading] = useState<{ identifier: boolean; website: boolean; brand: boolean }>({
        identifier: !!prefill?.loading?.identifier,
        website: !!prefill?.loading?.website,
        brand: !!prefill?.loading?.brand,
    });
    const payloadIdentifierRef = useRef<string>('');
    const isAddUsingFlowRef = useRef<boolean>(false);

    useEffect(() => {
        if (!modalOpen) return;
        const resolvedSource = source || prefill?.source || 'unknown';
        Mixpanel.track('lead_add_modal_opened', {
            source: resolvedSource,
            userEmail: userEmail || '',
            current_url: window.location.href,
        });
    }, [modalOpen, prefill?.source, source, userEmail]);

    useEffect(() => {
        if (!modalOpen) return;
        const resolved = {
            displayIdentifier: prefill?.identifierDisplay || '',
            payloadIdentifier: prefill?.identifierPayload || '',
            websiteValue: prefill?.website || '',
            brandValue: prefill?.brand || '',
            prefillLoading: prefill?.loading || { identifier: false, website: false, brand: false },
        };
        isAddUsingFlowRef.current = !!prefill?.isAddUsingSearchText;
        payloadIdentifierRef.current = resolved.payloadIdentifier;
        setFormData({
            [WEBSITE]: resolved.websiteValue,
            [IDENTIFIER]: resolved.displayIdentifier,
            brandName: resolved.brandValue,
        });
        setFormErrors({ [WEBSITE]: '', [IDENTIFIER]: '', brandName: '' });
        setSearchText(resolved.displayIdentifier);
        setShowGlobalDropdown(!!prefill?.shouldOpenDropdown && resolved.displayIdentifier.length >= 3);
        const normalizedLoading = {
            identifier: !!resolved.prefillLoading?.identifier,
            website: !!resolved.prefillLoading?.website,
            brand: !!resolved.prefillLoading?.brand,
        };
        setPrefillLoading(normalizedLoading);
        setSelectedAddNewResult(resolved.displayIdentifier ? prefill?.baseResult || null : null);
    }, [modalOpen, prefill]);

    const closeWithReason = (reason: string) => {
        if (typeof closeModal === 'function') {
            closeModal(false);
        }
    };

    const changeFormValue = (value, key) => {
        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    /**
     * Handles a selection from global search:
     * - Normalizes org name/website into form fields and clears validation errors.
     * - Preserves search text for follow-up queries and hides dropdown/UI chrome.
     * - Business logic: routes "already added" selections to either inline success or navigation;
     *   for "add new" selections it clears stale autofill, shows loading, fetches company info,
     *   and rehydrates identifier/brand fields from the fetched payload.
     */
    const handleGlobalSelect = async (result, groupKey?: string) => {
        const orgName = result?.orgName || result?.orgDisplayName || '';
        const websiteUrl = result?.websiteUrl || '';
        const identifierValue = extractIdentifier(result, orgName);
        payloadIdentifierRef.current = identifierValue;
        // Picking a real entity means we're no longer "adding by typed text", so reset the mode
        // flag. addToWatchlist then resolves to payloadIdentifierRef (the entity's CIN/PAN/GSTIN)
        // instead of the typed display name. Symmetric with typing, which clears the picked state.
        isAddUsingFlowRef.current = false;
        changeFormValue(orgName, IDENTIFIER); // keep UI showing name
        changeFormValue(websiteUrl, WEBSITE);
        setFormErrors((errors) => ({ ...errors, [IDENTIFIER]: '', [WEBSITE]: '' }));
        setShowGlobalDropdown(false);
        setSearchResults(null);
        // Keep user-entered search text for follow-up calls; avoid toggling dropdown reopen loop.
        setSearchText(orgName);
        const isAddNew = groupKey === GLOBAL_SEARCH_GROUPS.ADD_NEW;
        setSelectedAddNewResult(isAddNew ? result : null);
        if (groupKey === GLOBAL_SEARCH_GROUPS.ALREADY_ADDED && result?.orgId) {
            if (handleAlreadyAddedInPlace) {
                onSuccess?.({
                    orgName: orgName,
                    assesseeOrgId: result.orgId,
                });
                closeWithReason('alreadyAdded-inplace');
            } else {
                navigateUser(result.orgId, result?.companyAccessType || '');
                closeWithReason('alreadyAdded-navigate');
            }
            return;
        }
        if (isAddNew) {
            // Clear old autofill values before fetching new info
            changeFormValue('', WEBSITE);
            changeFormValue('', 'brandName');
            setInfoLoading({ website: true, brand: true });
            try {
                const companyInfo = await fetchCompanyInfoForResult(result, orgName);
                // Recompute payload identifier when company info arrives (prefer CIN/PAN/legal_name)
                const updatedPayloadIdentifier = extractIdentifier(result, orgName);
                payloadIdentifierRef.current = updatedPayloadIdentifier;
                changeFormValue(companyInfo?.website || '', WEBSITE);
                // Do NOT use legal_name for brand; only brand_name if present.
                changeFormValue(companyInfo?.brand_name || '', 'brandName');
            } finally {
                setInfoLoading({ website: false, brand: false });
            }
        }
    };

    const hasFieldLoader =
        infoLoading.website ||
        infoLoading.brand ||
        searchLoading ||
        prefillLoading.identifier ||
        prefillLoading.website ||
        prefillLoading.brand;
    const isSubmitDisabled = loading || hasFieldLoader || hasError || !investorOrgId || !formData[IDENTIFIER]?.trim();
    const submitButtonText = 'Confirm';
    const isIdentifierPrefillLoading = prefillLoading.identifier;

    /**
     * Submits the add-to-watchlist form:
     * - Tracks the click, validates website format, and determines the identifier payload
     *   based on flow (direct user input vs. prefill/legal name fallback).
     * - Business logic: forwards composed payload to backend, then on success ensures an
     *   assesseeOrgId exists, resets form, propagates lead data via onSuccess, and closes modal;
     *   otherwise surfaces identifier errors and toggles loading appropriately.
     */
    const addToWatchlist = async () => {
        Mixpanel.track(INVESTOR_ADD_TO_WATCHLIST_CLICK);
        if (formData[WEBSITE] && !formData[WEBSITE]?.includes('.')) {
            setFormErrors((errors) => ({ ...errors, [WEBSITE]: WEBSITE_ERROR_MSG }));
            return;
        }
        const legalNameFromPrefill =
            prefill?.companyInfo?.legal_name ||
            prefill?.companyInfo?.data?.legal_name ||
            prefill?.baseResult?.companyInfo?.legal_name ||
            prefill?.baseResult?.companyInfo?.data?.legal_name;
        // isAddUsingFlowRef is true only for a genuine add-by-typed-text flow. Picking a real entity
        // (global bar or modal dropdown) resets it to false in handleGlobalSelect, so a pick resolves
        // to payloadIdentifierRef — the entity's CIN/PAN/GSTIN — rather than the typed display name.
        const identifierForPayload = resolveAddLeadIdentifier({
            isAddUsingFlow: isAddUsingFlowRef.current,
            displayName: formData[IDENTIFIER],
            payloadIdentifier: payloadIdentifierRef.current,
            legalNameFromPrefill,
        });
        setLoading(true);
        await addCompanyToWatchlist(
            {
                ...(formData[WEBSITE] && { websiteUrl: formData[WEBSITE] }),
                investorOrgId: investorOrgId,
                journeyName: journey,
                ...(identifierForPayload && { identifier: identifierForPayload }),
                ...(formData.brandName && { brandName: formData.brandName }),
                ...(inviteCode && { inviteCode }),
                ...(selectedAddNewResult && { searchResultContext: selectedAddNewResult }),
                // Tri-state: "INDIVIDUAL"=force individual, "COMPANY"=force company-from-entity-search, omit=BE auto-decision.
                // Send only when the user picked a card whose isIndividualPan is explicitly defined.
                ...(typeof selectedAddNewResult?.isIndividualPan === 'boolean' && {
                    addAsIndividualPan: selectedAddNewResult.isIndividualPan ? 'INDIVIDUAL' : 'COMPANY',
                }),
            },
            {
                onSuccess: (data) => {
                    if (!data?.orgInfo?.assesseeOrgId) {
                        setFormErrors((errors) => ({ ...errors, [IDENTIFIER]: IDENTIFY_ERROR_MSG }));
                        setLoading(false);
                        return;
                    }
                    setFormData({ [WEBSITE]: '', [IDENTIFIER]: '', brandName: '' });
                    const leadDataPayload = data?.orgInfo
                        ? {
                              orgName: data?.orgInfo?.orgName || formData[IDENTIFIER],
                              assesseeOrgId: data?.orgInfo?.assesseeOrgId,
                          }
                        : null;
                    onSuccess?.(leadDataPayload);
                    closeWithReason('submit-success');
                    setLoading(false);
                },
                onError: () => {
                    setLoading(false);
                },
            },
        );
    };

    return (
        <Modal open={modalOpen ?? true} onClose={() => closeWithReason('modal-onClose')} size="flexible" persistent>
            <div className={styles.AddToWatchlist}>
                <div className={styles.Title}>Add Lead</div>
                {!investorOrgId && (
                    <div className={styles.IdentifierFound} style={{ color: '#df666a', marginTop: '12px' }}>
                        <img src={infoPurpleIcon} alt="" height={'10px'} style={{ filter: 'hue-rotate(180deg)' }} />
                        Organization ID is missing. Please refresh the page.
                    </div>
                )}

                <div style={{ position: 'relative', marginTop: '24px' }} className={styles.FieldWithLoader}>
                    <Input
                        placeholder={'Name, CIN, LLPIN, PAN or GSTIN (min 3 char)'}
                        value={isIdentifierPrefillLoading ? 'Fetching...' : formData[IDENTIFIER]}
                        onChange={(val) => {
                            setFormErrors((errors) => ({ ...errors, [WEBSITE]: '', [IDENTIFIER]: '' }));
                            changeFormValue(val, IDENTIFIER);
                            payloadIdentifierRef.current = '';
                            setSearchText(val);
                            setShowGlobalDropdown(true);
                            setSelectedAddNewResult(null);
                        }}
                        errorMessage={formErrors[IDENTIFIER]}
                        isValid={!formErrors[IDENTIFIER]}
                        disabled={loading}
                        inputStyle={{ fontWeight: '550' }}
                        allowAllCharacters
                    />
                    {(searchLoading && showGlobalDropdown) || prefillLoading.identifier ? (
                        <span className={styles.FieldLoaderOverlay} aria-label="Searching" role="status">
                            <span className={styles.FieldLoader} />
                        </span>
                    ) : null}
                    <AddToWatchlistDropdown
                        visible={showGlobalDropdown}
                        searchText={searchText}
                        searchResults={searchResults}
                        onSelectAlreadyAdded={(result) => handleGlobalSelect(result, GLOBAL_SEARCH_GROUPS.ALREADY_ADDED)}
                        onSelectAddNew={(result) => handleGlobalSelect(result, GLOBAL_SEARCH_GROUPS.ADD_NEW)}
                        onFooterAddNew={() => {
                            changeFormValue(searchText, IDENTIFIER);
                            setFormErrors((errors) => ({ ...errors, [IDENTIFIER]: '', [WEBSITE]: '' }));
                            setShowGlobalDropdown(false);
                            setSearchResults(null);
                            setSelectedAddNewResult(null);
                        }}
                        footerLabel={`Add new organisation "${searchText}"`}
                        useWideLayout
                        wrapperClassName={styles.GlobalDropdownWrapper}
                        wrapperStyle={{
                            top: 'calc(100% + 0.5rem)',
                            maxHeight: '18rem',
                            boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
                            zIndex: 5,
                        }}
                    />
                </div>

                <div className={styles.FieldWithLoader}>
                    <Input
                        placeholder="Website (Optional)"
                        value={infoLoading.website || prefillLoading.website ? 'Fetching...' : formData[WEBSITE]}
                        onChange={(val) => {
                            setFormErrors((errors) => ({ ...errors, [WEBSITE]: '', [IDENTIFIER]: '' }));
                            changeFormValue(val, WEBSITE);
                        }}
                        style={{ marginTop: '16px' }}
                        disabled={loading || infoLoading.website}
                        readOnly={infoLoading.website}
                        isValid={!formErrors[WEBSITE]}
                        errorMessage={formErrors[WEBSITE]}
                        allowAllCharacters={true}
                    />
                    {infoLoading.website || prefillLoading.website ? (
                        <span className={styles.FieldLoaderOverlay} aria-label="Loading website" role="status">
                            <span className={styles.FieldLoader} />
                        </span>
                    ) : null}
                </div>

                <div className={`${styles.FieldWithLoader} ${styles.LastField}`}>
                    <Input
                        placeholder="Brand Name (Optional)"
                        value={infoLoading.brand || prefillLoading.brand ? 'Fetching...' : formData.brandName}
                        onChange={(val) => {
                            setFormErrors((errors) => ({ ...errors, brandName: '' }));
                            changeFormValue(val, 'brandName');
                        }}
                        style={{ marginTop: '16px' }}
                        disabled={loading || infoLoading.brand}
                        readOnly={infoLoading.brand}
                        isValid={!formErrors.brandName}
                        errorMessage={formErrors.brandName}
                        allowAllCharacters={true}
                    />
                    {infoLoading.brand || prefillLoading.brand ? (
                        <span className={styles.FieldLoaderOverlay} aria-label="Loading brand name" role="status">
                            <span className={styles.FieldLoader} />
                        </span>
                    ) : null}
                </div>

                <Button
                    disabled={isSubmitDisabled}
                    variant="primary"
                    onClick={addToWatchlist}
                    text={submitButtonText}
                    style={{ width: '100%' }}
                    loading={loading}
                />
            </div>
        </Modal>
    );
}

export default AddToWatchlistV2;
